import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Drawer from "antd/es/drawer";
import Form from "antd/es/form";
import type Calendar from "nylas/lib/models/calendar";
import { useIntl } from "react-intl";

import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import NylasForm from "@app/scene/settings/type/NylasForm";
import {
  areCalendarsLoading,
  getCalendars,
  isNylasCalendarSetupFinished,
  isNylasUpdating,
} from "@app/store/nylas";
import { fetchCalendars, updateNylas } from "@app/store/nylas/actions";
import { RootState } from "@app/store/rootReducer";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import useHiddenChat from "@app/util/useHiddenChat";

import Fields from "./component/Fields";
import styles from "./CreateEditPane.module.scss";
import useCloseConfirmationModal from "./util/useCloseConfirmationModal";
import useHandleFinish from "./util/useHandleFinish";

interface Props {
  analyticIssuer?: AnalyticsService;
  areCalendarsLoading: boolean;
  calendars: Calendar[];
  fetchNylasCalendars: typeof fetchCalendars.request;
  isNylasUpdating: boolean;
  isSetupFinished: boolean;
  nylasInfo: NylasInfo;
  onHide: () => void;
  updateNylas: typeof updateNylas.request;
}

const CreateEditPane: React.FC<Props> = ({
  analyticIssuer,
  areCalendarsLoading,
  calendars,
  fetchNylasCalendars,
  isNylasUpdating,
  isSetupFinished,
  nylasInfo,
  onHide,
  updateNylas,
}) => {
  const intl = useIntl();
  useEffect(() => {
    if (nylasInfo) {
      fetchNylasCalendars();
    }
  }, [fetchNylasCalendars, nylasInfo]);
  useHiddenChat();

  const [form] = Form.useForm<NylasForm>();
  const confirmClose = useCloseConfirmationModal(intl);
  const handleHide = useCallback(() => {
    if (isSetupFinished) {
      onHide();
    } else {
      confirmClose(onHide);
    }
  }, [confirmClose, isSetupFinished, onHide]);

  const handleSubmit = useHandleFinish(form, intl, onHide, updateNylas, nylasInfo, analyticIssuer);

  useEffect(() => {
    if (!areCalendarsLoading && calendars.length && !form.getFieldValue("calendarId")) {
      form.setFieldsValue({ calendarId: calendars[0].id });
    }
  }, [areCalendarsLoading, calendars, form]);

  if (!nylasInfo) {
    return null;
  }

  return (
    <Drawer
      className={styles.container}
      closable
      onClose={handleHide}
      placement="right"
      title={
        isSetupFinished
          ? intl.formatMessage({
              id: "settings.calendar.createEditPane.title",
              defaultMessage: "Edit Calendar Sync",
              description: "Settings calendar create/edit pane - title",
            })
          : intl.formatMessage({
              id: "settings.calendar.createEditPane.title.notSaved",
              defaultMessage: "Set up Calendar Sync",
              description: "Settings calendar create/edit pane - title",
            })
      }
      visible
    >
      {areCalendarsLoading ? (
        <LoadingSpinner global />
      ) : (
        <Form
          className={styles.form}
          form={form}
          initialValues={{
            calendarCrmActivityType: nylasInfo?.calendarCrmActivityType,
            calendarId: nylasInfo?.calendarId ?? calendars[0]?.id,
            nylasCrmActivityTypes: nylasInfo.nylasCrmActivityTypes,
            recurActivitySync: nylasInfo.recurActivitySync,
            syncNotes: nylasInfo.syncNotes,
            syncOptions: nylasInfo.syncOptions,
            syncPreference: nylasInfo.syncPreference,
          }}
          onFinish={handleSubmit}
        >
          <Fields
            analyticIssuer={analyticIssuer}
            calendars={calendars}
            form={form}
            intl={intl}
            nylasInfo={nylasInfo}
            onHide={onHide}
          />
          <div className={styles.bottom}>
            <Button
              disabled={isNylasUpdating}
              htmlType="submit"
              loading={isNylasUpdating}
              type="primary"
            >
              {isSetupFinished
                ? intl.formatMessage({
                    id: "settings.calendar.createEditPane.save",
                    defaultMessage: "Save Changes",
                    description: "Settings calendar create/edit pane - save",
                  })
                : intl.formatMessage({
                    id: "settings.calendar.createEditPane.start",
                    defaultMessage: "Save and start sync",
                    description: "Settings calendar create/edit pane - start",
                  })}
            </Button>
          </div>
        </Form>
      )}
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  areCalendarsLoading: areCalendarsLoading(state),
  calendars: getCalendars(state),
  isNylasUpdating: isNylasUpdating(state),
  isSetupFinished: isNylasCalendarSetupFinished(state),
});

const mapDispatchToProps = {
  fetchNylasCalendars: fetchCalendars.request,
  updateNylas: updateNylas.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEditPane);
